import { memo, useState, useCallback } from "react"
import { Box, Container, HStack, Stack } from "@chakra-ui/react"
import { useRoutes } from "@app/hooks/useRoutes"
import { QuotationText } from "@components/Sections/Quotation"
import { useMedia } from "@app/hooks/useMedia"
import { TestimonialsArrowButton } from "./TestimonialsArrowButton"
import { TestimonialsLink } from "./TestimonialsLink"
import { TestimonialsQuotee } from "./TestimonialsQuotee"
import { TestimonialsImage } from "./TestimonialsImage"

const Testimonials: React.FC<GatsbyTypes.SanitySectionTestimonials> = ({ bgColor, testimonials, link }) => {
  const [activeTestimonial, setActiveTestimonial] = useState(0)
  const { urlResolver } = useRoutes()
  const resolvedUrl = urlResolver(link)
  const { isMedium } = useMedia()

  const testimonialCount = testimonials?.length || 0

  const onlyOneTestimonial = testimonialCount === 1

  const handleBackClick = useCallback(() => {
    const isFirstTestimonial = activeTestimonial === 0
    if (isFirstTestimonial) return null
    return setActiveTestimonial(activeTestimonial - 1)
  }, [activeTestimonial])

  const handleForwardClick = useCallback(() => {
    const isLastTestimonial = activeTestimonial === testimonialCount - 1
    if (isLastTestimonial) return null
    return setActiveTestimonial(activeTestimonial + 1)
  }, [activeTestimonial, testimonialCount])

  if (!testimonialCount) return null

  return (
    <Box bg={bgColor} py={[11, 11, 32]}>
      <Container>
        {testimonials?.map((testimonial, idx) => (
          <Stack
            direction={["column", "column", "row"]}
            spacing={[6, 6, 10, 24]}
            key={testimonial?._key}
            w="full"
            display={idx === activeTestimonial ? "flex" : "none"}
            justifyContent="center"
            alignItems={[null, null, "center"]}
          >
            {!isMedium && (
              <>
                <TestimonialsLink link={resolvedUrl} />
                <HStack spacing={4}>
                  <TestimonialsImage testimonial={testimonial} small />
                  <Box position="relative" pl={5}>
                    <QuotationText text={testimonial?.quotation} size="h4" />
                  </Box>
                </HStack>
              </>
            )}
            {isMedium && <TestimonialsImage testimonial={testimonial} />}
            <Box pl={[0, 0, 10]} position="relative" maxW={99}>
              {isMedium && (
                <>
                  <TestimonialsLink link={resolvedUrl} />
                  <QuotationText text={testimonial?.quotation} size="h2" />
                </>
              )}
              {!onlyOneTestimonial && (
                <HStack mt={[0, 0, 10]} mb={[6, 6, 8]} spacing={3.5}>
                  <TestimonialsArrowButton handleClick={handleBackClick} disabled={activeTestimonial === 0} back />
                  <TestimonialsArrowButton handleClick={handleForwardClick} disabled={activeTestimonial === testimonialCount - 1} />
                </HStack>
              )}
              <TestimonialsQuotee testimonial={testimonial} />
            </Box>
          </Stack>
        ))}
      </Container>
    </Box>
  )
}

export default memo(Testimonials)
